import { computed, ref } from 'vue'
import { createGlobalState, useSwipe } from '@vueuse/core'
import { useDeviceWidth } from '@/composables/useDeviceWidth'
import { widthMapping } from '@/constants/mappings/width.mapping'
import { queryClient } from '@/plugins/vue-query'
import { NESTED_SCROLL_PREVENTED_SELECTOR } from '@/constants/scrollable-overlay.const'

const PULL_THRESHOLD_PX = 72 // 比 swipe-back 的 96 略小，下拉手勢通常距離較短
const MAX_PULL_PX = 120 // 白底區塊最大高度，避免無限拉長

/**
 * 手機版向下拉動重新整理：只在頁面已經捲到最頂端時才啟用，避免跟一般向下捲動（露出更
 * 上面的內容）衝突——這是下拉重整跟一般捲動唯一的區分依據。放開時如果拉動距離達門檻，
 * 呼叫 queryClient.invalidateQueries()（不帶條件，讓目前畫面上所有 active 的 query
 * 全部失效並重新請求）。
 */
export const usePullToRefresh = createGlobalState(() => {
  const { width } = useDeviceWidth()
  const isMobile = computed(() => width.value <= widthMapping['md'])

  // 手勢開始當下是否已經捲到頂端；不是的話這次手勢全程都不算數（維持一般捲動）
  const startedAtTop = ref(false)
  const isRefreshing = ref(false)

  // 其他手勢（例如 group-edit 的拖曳排序）進行中時暫時讓出：那段期間手指的上下移動
  // 屬於該手勢，不該被當成下拉重整
  const isSuppressed = ref(false)

  const { isSwiping, direction, lengthY } = useSwipe(window, {
    threshold: PULL_THRESHOLD_PX,
    onSwipeStart: (e) => {
      // 手勢起點落在下拉選單這類浮層的捲動區域時，那是該區域自己的捲動，不是下拉重整。
      // 這裡監聽的是 window，不受 Lenis 的 prevent 影響，必須自己判斷；而且 Lenis 接管後
      // window.scrollY 恆為 0，「在頂端」永遠成立，不擋的話在選單上往下滑就會誤觸重整
      const target = e.target as HTMLElement | null
      if (target?.closest?.(NESTED_SCROLL_PREVENTED_SELECTOR)) {
        startedAtTop.value = false
        return
      }

      startedAtTop.value = window.scrollY <= 0
    },
    onSwipeEnd: (_e, endDirection) => {
      if (isSuppressed.value || !startedAtTop.value || endDirection !== 'down' || !isMobile.value)
        return

      isRefreshing.value = true
      queryClient.invalidateQueries().finally(() => {
        isRefreshing.value = false
      })
    }
  })

  const isPullingDown = computed(
    () =>
      !isSuppressed.value &&
      isMobile.value &&
      startedAtTop.value &&
      window.scrollY <= 0 &&
      isSwiping.value &&
      direction.value === 'down'
  )

  const isActive = computed(() => isPullingDown.value || isRefreshing.value)

  // lengthY = coordsStart.y - coordsEnd.y，往下拉時是負值，取負號還原成正值拉動距離
  const pullDistance = computed(() => {
    if (isRefreshing.value) return PULL_THRESHOLD_PX
    if (!isPullingDown.value) return 0
    return Math.min(Math.max(-lengthY.value, 0), MAX_PULL_PX)
  })

  // 拖曳中要即時跟手（不能有 transition），放開瞬間才切換成有 transition 讓它彈回或收合
  const isDragging = computed(() => isPullingDown.value)

  return { isActive, isRefreshing, pullDistance, isDragging, isSuppressed }
})
